import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Eye, EyeOff, RefreshCw, Trash2 } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const SOURCE_PILLS = { curated: ["locked", "CURATED"], pyq: ["review", "NEET PYQ"], ai: ["approved", "AI"], pdf: ["paid", "PDF PAPER"] };

export default function AdminQuestions({ topicFilter, setTopicFilter }) {
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const params = topicFilter ? { topic_id: topicFilter } : {};
    const [{ data: q }, { data: t }] = await Promise.all([adminApi.get("/admin/test/questions", { params }), adminApi.get("/admin/test/topics")]);
    setQuestions(q.questions);
    setTopics(t.topics);
  }, [topicFilter]);
  useEffect(() => { load().catch(() => setError("Could not load the question bank. Click Refresh or try again.")); }, [load]);

  const toggleActive = async (q) => {
    try {
      await adminApi.patch(`/admin/test/questions/${q.question_id}`, { active: !q.active });
      setNotice(q.active ? "Question hidden from quizzes." : "Question re-activated.");
      await load();
    } catch (err) { setError(errText(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this question permanently?")) return;
    await adminApi.delete(`/admin/test/questions/${id}`);
    await load();
  };

  const visible = questions.filter((q) => !sourceFilter || (q.source || "curated") === sourceFilter);
  const activeInView = visible.filter((q) => q.active).length;

  return (
    <div data-testid="admin-questions">
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}
      {error && <p className="admin-error" data-testid="q-warning">{error}</p>}
      <div className="admin-card" style={{ marginBottom: 16, borderLeft: "3px solid #f2c14e" }}>
        <b style={{ color: "#f2c14e" }}>Questions are now generated automatically.</b>{" "}
        <span className="lead-sub">New questions come from the AI engine and the verified NEET PYQ pool — use the <b>AI Studio</b> tab to configure generation. Here you can review the full bank, hide weak questions, or delete them. A topic's test stays live while it has 5+ active questions.</span>
      </div>
      <div className="admin-row" style={{ marginBottom: 16 }}>
        <button className="admin-btn" onClick={() => load()} data-testid="q-refresh"><RefreshCw size={14} /> Refresh</button>
        <select className="admin-input" style={{ maxWidth: 220 }} value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} data-testid="q-topic-filter">
          <option value="">All topics</option>
          {topics.map((t) => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
        </select>
        <select className="admin-input" style={{ maxWidth: 180 }} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="q-source-filter">
          <option value="">All sources</option>
          <option value="curated">Curated bank</option>
          <option value="pyq">NEET PYQ</option>
          <option value="ai">AI generated</option>
          <option value="pdf">PDF paper</option>
        </select>
        <span className="lead-sub" data-testid="q-count">{visible.length} shown · {activeInView} active</span>
      </div>
      {visible.map((q, i) => {
        const src = q.source || "curated";
        const pill = SOURCE_PILLS[src] || SOURCE_PILLS.curated;
        const open = expanded === q.question_id;
        return (
          <div className="admin-card" key={q.question_id} data-testid={`q-row-${i}`}>
            <div className="admin-row">
              <span className={`status-pill ${pill[0]}`} data-testid={`q-source-${i}`}>{src === "ai" ? (q.verified ? "AI · VERIFIED" : "AI · PENDING") : pill[1]}</span>
              {q.flagged && <span className="status-pill failed" data-testid={`q-flagged-${i}`}>FLAGGED</span>}
              <span className="status-pill locked" data-testid={`q-topicname-${i}`}>{q.topic_name}{q.subtopic ? ` · ${q.subtopic}` : ""}</span>
              <span style={{ flex: 1, color: "#dbe6f4", fontSize: 12 }}>{q.text.slice(0, 110)}{q.text.length > 110 ? "…" : ""}</span>
              <span className={q.active ? "status-pill approved" : "status-pill locked"} data-testid={`q-active-${i}`}>{q.active ? "ACTIVE" : "HIDDEN"}</span>
            </div>
            <div className="admin-row" style={{ marginTop: 10 }}>
              <span className="lead-sub">Answer: {q.correct} · {q.difficulty}{q.pyq_ref ? ` · ${q.pyq_ref}` : ""}{q.ai_provider ? ` · by ${q.ai_provider}` : ""}</span>
              <button className="admin-btn" onClick={() => setExpanded(open ? null : q.question_id)} data-testid={`q-view-${i}`}>{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "View"}</button>
              <button className="admin-btn" onClick={() => toggleActive(q)} data-testid={`q-toggle-${i}`}>{q.active ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Activate</>}</button>
              <button className="admin-btn" onClick={() => remove(q.question_id)} data-testid={`q-delete-${i}`}><Trash2 size={13} /></button>
            </div>
            {open && <div style={{ marginTop: 12, borderTop: "1px solid #22314a", paddingTop: 12 }} data-testid={`q-detail-${i}`}>
              <p style={{ color: "#eef4fc", fontSize: 14, fontWeight: 600 }}>{q.text}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 6, margin: "10px 0" }}>
                {["A", "B", "C", "D"].map((k) => <div key={k} style={{ fontSize: 13, padding: "7px 10px", borderRadius: 8, background: k === q.correct ? "#123a2e" : "#16223a", color: k === q.correct ? "#4ade80" : "#c6d4e8" }}><b>{k}.</b> {q.options?.[k]}</div>)}
              </div>
              {q.explanation && <p className="lead-sub" style={{ marginBottom: 6 }}><b style={{ color: "#9db1c9" }}>Explanation:</b> {q.explanation}</p>}
              {q.solution && <p className="lead-sub"><b style={{ color: "#9db1c9" }}>Solution:</b> {q.solution}</p>}
              {q.flag_reason && <p className="status-pill failed" style={{ display: "inline-block", marginTop: 8 }}>{q.flag_reason}</p>}
            </div>}
          </div>
        );
      })}
      {visible.length === 0 && <div className="admin-empty">No questions match these filters. Generate fresh ones in the AI Studio tab.</div>}
    </div>
  );
}
